import type { EntitlementSnapshot } from './entitlements';

export type PreviewTrialBillingInterval = 'monthly' | 'yearly';
export const PREVIEW_TRIAL_VERIFICATION_TIMEOUT_MS = 10_000;

export type PreviewTrialRouteParams = {
    preview_project_id?: string | string[];
    result_digest_id?: string | string[];
    billing?: string | string[];
};

export interface PreviewTrialSelector {
    projectId: string;
    resultDigestId: string | null;
    billingInterval: PreviewTrialBillingInterval;
    hasExplicitBilling: boolean;
}

export type LegacyTrialRouteDecision =
    | { kind: 'pricing' }
    | { kind: 'generic'; href: string }
    | { kind: 'preview'; href: string; selector: PreviewTrialSelector };

export type PreviewTrialClassification =
    | {
        kind: 'checkout';
        projectId: string;
        resultDigestId: string;
        trackingState: 'paused' | 'pausing';
    }
    | {
        kind: 'preparing';
        projectId: string;
        resultDigestId: string | null;
    }
    | { kind: 'no_result' }
    | {
        kind: 'already_entitled';
        projectId: string;
        resultDigestId: string | null;
    }
    | {
        kind: 'unavailable';
        reason: 'context_mismatch' | 'incoherent_snapshot' | 'untrusted_snapshot';
    };

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const AUTHORITATIVE_SOURCES = new Set(['free', 'pre_billing_preview', 'subscription', 'legacy_grace']);
const ACTIVE_PREVIEW_STATUSES = new Set(['configured', 'running']);
const POSITIVE_RESULT_KINDS = new Set(['must_read', 'related_only']);
const CHECKOUT_RECLASSIFICATION_CODES = new Set([
    'preview_project_not_checkout_ready',
    'active_subscription_exists',
]);

export interface PreviewTrialVerificationAttempt<T> {
    promise: Promise<T>;
    cancel: () => void;
}

export class PreviewTrialVerificationTimeoutError extends Error {
    constructor() {
        super('Preview trial verification timed out.');
        this.name = 'PreviewTrialVerificationTimeoutError';
    }
}

function strictSingleParam(value: string | string[] | undefined): string | null {
    return typeof value === 'string' && value.length > 0 ? value : null;
}

function normalizeUuid(value: unknown): string | null {
    return typeof value === 'string' && UUID_PATTERN.test(value)
        ? value.toLowerCase()
        : null;
}

function optionalString(value: unknown): { valid: boolean; value: string | null } {
    if (value === null || value === undefined) {
        return { valid: true, value: null };
    }
    if (typeof value !== 'string' || !value.trim()) {
        return { valid: false, value: null };
    }
    return { valid: true, value: value.trim() };
}

function optionalTimestamp(value: unknown): { valid: boolean; value: string | null } {
    const parsed = optionalString(value);
    if (!parsed.valid || !parsed.value) {
        return parsed;
    }
    return Number.isFinite(Date.parse(parsed.value))
        ? parsed
        : { valid: false, value: null };
}

function truthy(value: unknown): boolean {
    if (value === true || value === 1) return true;
    return typeof value === 'string' && ['true', 't', '1', 'yes', 'on'].includes(value.trim().toLowerCase());
}

function hasAlreadyEntitledAccess(snapshot: EntitlementSnapshot, status: string | null, convertedAt: string | null): boolean {
    const source = optionalString(snapshot.derived?.entitlement_source).value;
    return source === 'subscription'
        || source === 'legacy_grace'
        || truthy(snapshot.derived?.is_friend)
        || truthy(snapshot.derived?.legacy_entitlement_grace)
        || status === 'converted'
        || Boolean(convertedAt);
}

export function shouldReclassifyPreviewTrialCheckoutError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;
    const code = (error as { code?: unknown }).code;
    return typeof code === 'string' && CHECKOUT_RECLASSIFICATION_CODES.has(code);
}

export function isRetryablePreviewTrialClassification(
    result: PreviewTrialClassification | null
): boolean {
    return result?.kind === 'unavailable' && result.reason !== 'context_mismatch';
}

export function startPreviewTrialVerification<T>(
    operation: Promise<T>,
    timeoutMs = PREVIEW_TRIAL_VERIFICATION_TIMEOUT_MS
): PreviewTrialVerificationAttempt<T> {
    let settled = false;
    let rejectAttempt: ((reason: Error) => void) | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const promise = new Promise<T>((resolve, reject) => {
        rejectAttempt = reject;
        timeoutId = setTimeout(() => {
            if (settled) return;
            settled = true;
            reject(new PreviewTrialVerificationTimeoutError());
        }, timeoutMs);
        operation.then((value) => {
            if (settled) return;
            settled = true;
            if (timeoutId !== null) clearTimeout(timeoutId);
            resolve(value);
        }, (error) => {
            if (settled) return;
            settled = true;
            if (timeoutId !== null) clearTimeout(timeoutId);
            reject(error);
        });
    });

    return {
        promise,
        cancel: () => {
            if (settled) return;
            settled = true;
            if (timeoutId !== null) clearTimeout(timeoutId);
            rejectAttempt?.(new Error('Preview trial verification cancelled.'));
        },
    };
}

export function parsePreviewTrialSelector(
    params: PreviewTrialRouteParams
): PreviewTrialSelector | null {
    const rawProjectId = strictSingleParam(params.preview_project_id);
    const rawDigestId = strictSingleParam(params.result_digest_id);
    const rawBilling = strictSingleParam(params.billing);
    const projectId = normalizeUuid(rawProjectId);
    const resultDigestId = rawDigestId ? normalizeUuid(rawDigestId) : null;
    const hasDigestParam = params.result_digest_id !== undefined;
    const hasBillingParam = params.billing !== undefined;

    if (
        !projectId
        || (hasDigestParam && !resultDigestId)
        || (hasBillingParam && rawBilling !== 'monthly' && rawBilling !== 'yearly')
    ) {
        return null;
    }

    return {
        projectId,
        resultDigestId,
        billingInterval: rawBilling === 'monthly' ? 'monthly' : 'yearly',
        hasExplicitBilling: hasBillingParam,
    };
}

export function buildPreviewTrialHref(selector: PreviewTrialSelector): string {
    const params = new URLSearchParams({ preview_project_id: selector.projectId });
    if (selector.resultDigestId) {
        params.set('result_digest_id', selector.resultDigestId);
    }
    if (selector.hasExplicitBilling) {
        params.set('billing', selector.billingInterval);
    }
    return `/trial/preview?${params.toString()}`;
}

export function buildGenericTrialHref(interval: PreviewTrialBillingInterval): string {
    return `/trial?checkout_interval=${interval}`;
}

export function resolveLegacyTrialRoute(
    params: PreviewTrialRouteParams
): LegacyTrialRouteDecision {
    const hasPreviewSelector = params.preview_project_id !== undefined
        || params.result_digest_id !== undefined;
    if (!hasPreviewSelector) {
        return { kind: 'pricing' };
    }

    const selector = parsePreviewTrialSelector(params);
    if (selector) {
        return { kind: 'preview', selector, href: buildPreviewTrialHref(selector) };
    }

    return { kind: 'generic', href: '/trial' };
}

export function classifyPreviewTrialSnapshot(
    snapshot: EntitlementSnapshot | null | undefined,
    selector: PreviewTrialSelector
): PreviewTrialClassification {
    if (!snapshot) {
        return { kind: 'unavailable', reason: 'untrusted_snapshot' };
    }

    const source = optionalString(snapshot.derived?.entitlement_source).value;
    if (
        truthy(snapshot.derived?.entitlement_fallback)
        || !source
        || !AUTHORITATIVE_SOURCES.has(source)
    ) {
        return { kind: 'unavailable', reason: 'untrusted_snapshot' };
    }

    const preview = snapshot.billing_preview;
    if (!preview || typeof preview !== 'object' || Array.isArray(preview)) {
        return { kind: 'unavailable', reason: 'incoherent_snapshot' };
    }

    const statusField = optionalString(preview.status);
    const digestField = optionalString(preview.result_digest_id);
    const resultKindField = optionalString(preview.result_kind);
    const resultFoundField = optionalTimestamp(preview.result_found_at);
    const deliveredField = optionalTimestamp(preview.result_delivered_at);
    const pausedField = optionalTimestamp(preview.paused_at);
    const convertedField = optionalTimestamp(preview.converted_at);
    if (
        !statusField.valid
        || !digestField.valid
        || !resultKindField.valid
        || !resultFoundField.valid
        || !deliveredField.valid
        || !pausedField.valid
        || !convertedField.valid
    ) {
        return { kind: 'unavailable', reason: 'incoherent_snapshot' };
    }

    const projectId = normalizeUuid(preview.project_id);
    const resultDigestId = digestField.value ? normalizeUuid(digestField.value) : null;
    if (
        !projectId
        || projectId !== selector.projectId
        || (digestField.value && !resultDigestId)
        || (selector.resultDigestId !== null && selector.resultDigestId !== resultDigestId)
    ) {
        return { kind: 'unavailable', reason: 'context_mismatch' };
    }

    const status = statusField.value;
    if (hasAlreadyEntitledAccess(snapshot, status, convertedField.value)) {
        return { kind: 'already_entitled', projectId, resultDigestId };
    }

    const resultKind = resultKindField.value;
    const resultFoundAt = resultFoundField.value;
    const resultDeliveredAt = deliveredField.value;
    const pausedAt = pausedField.value;
    const requiresCheckout = preview.requires_checkout === true;
    const isActive = Boolean(status && ACTIVE_PREVIEW_STATUSES.has(status));
    const isPositiveResult = Boolean(resultKind && POSITIVE_RESULT_KINDS.has(resultKind));

    if (resultKind && !isPositiveResult) {
        return { kind: 'unavailable', reason: 'incoherent_snapshot' };
    }

    if (!resultKind) {
        const hasAnyResultEvidence = Boolean(resultDigestId || resultFoundAt || resultDeliveredAt);
        if (status === 'paused' && pausedAt && requiresCheckout && !hasAnyResultEvidence) {
            return { kind: 'no_result' };
        }
        if (isActive && !pausedAt && !hasAnyResultEvidence) {
            return { kind: 'preparing', projectId, resultDigestId: null };
        }
        return { kind: 'unavailable', reason: 'incoherent_snapshot' };
    }

    if (!resultDigestId || !resultFoundAt || !resultDeliveredAt) {
        return isActive && !pausedAt
            ? { kind: 'preparing', projectId, resultDigestId }
            : { kind: 'unavailable', reason: 'incoherent_snapshot' };
    }

    if (status === 'paused' && pausedAt && requiresCheckout) {
        return {
            kind: 'checkout',
            projectId,
            resultDigestId,
            trackingState: 'paused',
        };
    }

    if (isActive && !pausedAt) {
        return {
            kind: 'checkout',
            projectId,
            resultDigestId,
            trackingState: 'pausing',
        };
    }

    return { kind: 'unavailable', reason: 'incoherent_snapshot' };
}
